import { useEffect, useState } from "react";

export const MEDELLIN_FALLBACK = { lat: 6.2476, lng: -75.5658 };

export type GeolocationState =
  | { status: "loading"; lat: number; lng: number }
  | { status: "granted"; lat: number; lng: number }
  | { status: "denied"; lat: number; lng: number }
  | { status: "unavailable"; lat: number; lng: number }
  | { status: "timeout"; lat: number; lng: number }
  | { status: "unsupported"; lat: number; lng: number };

function hasGeolocation(): boolean {
  return typeof navigator !== "undefined" && !!navigator.geolocation;
}

function statusForCode(
  code: number,
): "denied" | "unavailable" | "timeout" {
  if (code === 1) return "denied";
  if (code === 3) return "timeout";
  return "unavailable";
}

export function useGeolocation(): GeolocationState {
  const [state, setState] = useState<GeolocationState>(() => ({
    status: hasGeolocation() ? "loading" : "unsupported",
    lat: MEDELLIN_FALLBACK.lat,
    lng: MEDELLIN_FALLBACK.lng,
  }));

  useEffect(() => {
    if (!hasGeolocation()) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setState({
          status: "granted",
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        const status = statusForCode(err.code);
        if (status !== "denied") {
          // Permission denial is user choice; the others are bugs/network issues worth tracing.
          console.warn("[geolocation]", status, err.message);
        }
        setState({ status, ...MEDELLIN_FALLBACK });
      },
      { timeout: 8000, enableHighAccuracy: false, maximumAge: 60_000 },
    );
  }, []);

  return state;
}
